import type { DropEvent, FileDropItem } from "react-aria";

import { CloudUpload, X } from "lucide-react";
import { useCallback, useState } from "react";
import { Dialog, DropZone, FileTrigger, Heading } from "react-aria-components";

import { Button } from "@/components/button";
import { Modal } from "@/components/modal";
import { toast } from "@/components/toast/toast-queue";
import { useBrandPresignedUpload } from "@/features/brands/shared/hooks/useBrandPresignedUpload";
import { useUpdateBrand } from "@/features/brands/shared/hooks/useUpdateBrand";

const ACCEPTED_TYPES = new Set(["image/png", "image/jpeg"]);
const MAX_BYTES = 10 * 1024 * 1024;

interface BrandIconUploadModalProps {
  brandId: number;
  currentIconUrl?: null | string;
  isOpen: boolean;
  onClose: () => void;
}

export function BrandIconUploadModal({
  brandId,
  currentIconUrl,
  isOpen,
  onClose,
}: BrandIconUploadModalProps) {
  const { isUploading, upload } = useBrandPresignedUpload();
  const updateBrand = useUpdateBrand({ brandId });
  const [isDropTarget, setIsDropTarget] = useState(false);

  const isPending = isUploading || updateBrand.isPending;

  const handleFile = useCallback(
    async (file: File) => {
      if (!ACCEPTED_TYPES.has(file.type)) {
        toast.add({
          description: "Only PNG or JPG files are accepted.",
          timeout: 3000,
          title: "Invalid file type",
          type: "error",
        });

        return;
      }
      if (file.size > MAX_BYTES) {
        toast.add({
          description: "Please choose a file smaller than 10MB.",
          timeout: 3000,
          title: "File too large",
          type: "error",
        });

        return;
      }

      const s3Key = await upload(file, "logo");

      if (s3Key) {
        updateBrand.mutate(
          { basicInfo: { profilePicture: s3Key } },
          { onSuccess: onClose },
        );
      }
    },
    [upload, updateBrand, onClose],
  );

  const handleDrop = async (e: DropEvent) => {
    try {
      const fileItem = e.items.find(
        (item): item is FileDropItem => item.kind === "file",
      );
      const droppedFile = await fileItem?.getFile();

      if (droppedFile) {
        await handleFile(droppedFile);
      }
    } catch {
      // upload/update errors are handled by useBrandPresignedUpload and useUpdateBrand via toast
    }
  };

  const handleRemove = () => {
    updateBrand.mutate(
      { basicInfo: { profilePicture: null } },
      { onSuccess: onClose },
    );
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog className="p-6 outline-none">
        <div className="mb-4 flex items-center justify-between">
          <Heading
            className="text-lg font-semibold text-text-primary"
            slot="title"
          >
            Update brand icon
          </Heading>
          <Button
            isDisabled={isPending}
            isIconOnly
            onPress={onClose}
            size="sm"
            variant="unstyled"
          >
            <X className="text-text-tertiary" size={16} />
          </Button>
        </div>

        <DropZone
          className={`flex w-full cursor-pointer flex-col items-center gap-2 rounded-2xl border px-8 py-6 text-center transition-all duration-200 ${
            isDropTarget
              ? "border-border-brand-solid bg-bg-brand-secondary"
              : "border-border-brand bg-bg-brand-primary hover:bg-bg-brand-secondary"
          }`}
          isDisabled={isPending}
          onDrop={handleDrop}
          onDropEnter={() => setIsDropTarget(true)}
          onDropExit={() => setIsDropTarget(false)}
        >
          <FileTrigger
            acceptedFileTypes={[".png", ".jpg", ".jpeg"]}
            onSelect={(files) => {
              const file = files ? [...files][0] : null;

              if (file) handleFile(file);
            }}
          >
            <Button
              className="w-full outline-hidden"
              isDisabled={isPending}
              variant="unstyled"
            >
              <div className="flex flex-col items-center gap-1">
                <CloudUpload className="text-text-brand-secondary" size={24} />
                <span className="text-sm font-medium text-text-primary">
                  {isPending
                    ? "Uploading..."
                    : "Click to upload or drag & drop"}
                </span>
                <span className="text-sm text-text-tertiary">
                  PNG or JPG up to 10MB
                </span>
              </div>
            </Button>
          </FileTrigger>
        </DropZone>

        {currentIconUrl && (
          <Button
            className="mt-3 w-full"
            color="destructive"
            isDisabled={isPending}
            onPress={handleRemove}
            variant="secondary"
          >
            Remove photo
          </Button>
        )}
      </Dialog>
    </Modal>
  );
}
